import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { 
  Sparkles, 
  Compass, 
  Heart, 
  MapPin, 
  Briefcase, 
  Building, 
  Mail, 
  Phone, 
  Check, 
  User, 
  ArrowRight,
  ShieldCheck,
  MessageCircle,
  Menu,
  X
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const PROFILES = [
  {
    name: "Sehaj Singh",
    company: "Arabian Networking Group",
    designation: "Founder & CEO",
    category: "Business Owner",
    industry: "Business Networking",
    location: "Dubai, UAE",
    bio: "Founder and CEO of Arabian Networking Group, connecting investors, business owners and senior professionals through premium networking experiences.",
    building: "Creating the GCC's leading multi-category business networking ecosystem.",
    looking: "Investors • Strategic Partners • Sponsors",
    image: "/sehaj.jpg"
  },
  {
    name: "Liana Vertov",
    company: "Northbridge Equity",
    designation: "Managing Director",
    category: "Investor",
    industry: "Private Equity & Family Office",
    location: "London, United Kingdom",
    bio: "Leading growth investments across a diversified global portfolio. Two decades of experience scaling enterprises and stewarding long-term institutional capital.",
    building: "Deploying a $300M growth fund across FinTech, healthcare and enterprise software.",
    looking: "Established business owners and operators seeking strategic growth capital and board-level partnership.",
    image: "https://images.unsplash.com/photo-1585240975858-7264fd020798?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxpbnZlc3RvciUyMHdvbWFuJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzgyNjYwNzI3fDA&ixlib=rb-4.1.0&q=85"
  },
  {
    name: "Idris Marlowe",
    company: "Meridian Advisory",
    designation: "Founder & Partner",
    category: "Business Owner",
    industry: "Strategy & Advisory",
    location: "Doha, Qatar",
    bio: "Founder and advisor guiding ambitious enterprises through scale. Trusted counsel to business owners, family offices and institutional investors across the region.",
    building: "A cross-border advisory practice bridging Gulf capital with global ventures.",
    looking: "Business owners and investors seeking board-level expertise and high-conviction co-investment.",
    image: "https://images.pexels.com/photos/26820703/pexels-photo-26820703.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
  }
];

export default function LandingPage() {
  const [formData, setFormData] = useState({
    fullName: "",
    company: "",
    designation: "",
    email: "",
    phone: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Smartphone animation state
  const [cardIndex, setCardIndex] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [isMatch, setIsMatch] = useState(false);
  // Match screen only appears later, once the visitor starts scrolling
  const matchEnabledRef = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 150) matchEnabledRef.current = true;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (isMatch) return;

    const interval = setInterval(() => {
      // Determine swipe direction: alternate left/right, show match on right swipes
      const nextDir = Math.random() > 0.4 ? "right" : "left";
      setSwipeDirection(nextDir);

      setTimeout(() => {
        // Move to next card
        setCardIndex((prev) => (prev + 1) % PROFILES.length);
        setSwipeDirection(null);

        // Occasional match popup after a right swipe, only once scrolled
        if (matchEnabledRef.current && nextDir === "right" && Math.random() > 0.3) {
          setIsMatch(true);
          setTimeout(() => {
            setIsMatch(false);
          }, 2000);
        }
      }, 600); // Wait for transition duration
    }, 4500);

    return () => clearInterval(interval);
  }, [isMatch]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.fullName || !formData.company || !formData.designation || !formData.email || !formData.phone) {
      toast.error("Please fill in all the required waitlist fields.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, {
        full_name: formData.fullName,
        company: formData.company,
        designation: formData.designation,
        email: formData.email,
        phone: formData.phone
      });
      toast.success("Successfully joined the SOMEWHERE waitlist!");
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      const errorMsg = err.response?.data?.detail || "An error occurred. Please try again.";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Get current, next, and bottom profiles for stacked cards visual
  const currentProfile = PROFILES[cardIndex];
  const nextProfile = PROFILES[(cardIndex + 1) % PROFILES.length];
  const bottomProfile = PROFILES[(cardIndex + 2) % PROFILES.length];

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-white relative overflow-hidden font-inter selection:bg-[#C8A24A]/30 selection:text-white">
      <Toaster position="top-center" theme="dark" richColors />
      
      {/* Background radial accent glow */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-radial-gradient from-[#C8A24A]/10 to-transparent pointer-events-none rounded-full blur-[120px]" />
      <div className="absolute bottom-[-100px] left-[-100px] w-[400px] h-[400px] bg-radial-gradient from-white/3 to-transparent pointer-events-none rounded-full blur-[100px]" />

      {/* Sticky Premium Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0B0B0F]/80 border-b border-white/5 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="brand-lockup">
            <img src="/ang-logo-gold.png" alt="Arabian Networking Group" className="h-10 sm:h-12 w-auto object-contain flex-shrink-0" />
            <div className="h-8 sm:h-9 w-px bg-white/10 flex-shrink-0" />
            <div className="flex flex-col min-w-0">
              <span className="text-base sm:text-xl tracking-[0.2em] sm:tracking-[0.25em] font-medium font-clash gold-text-metallic leading-none truncate">SOMEWHERE</span>
              <span className="text-[8px] sm:text-[9px] uppercase tracking-[0.15em] text-[#B0B0B0] font-medium font-inter mt-1 truncate">
                by Arabian Networking Group
              </span>
            </div>
          </div>
          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6 flex-shrink-0">
            <a href="#how-it-works" className="text-sm text-[#B0B0B0] hover:text-[#C8A24A] transition-colors duration-200">
              How It Works
            </a>
            <a href="#join-the-waitlist" className="text-sm text-[#B0B0B0] hover:text-[#C8A24A] transition-colors duration-200">
              Waitlist
            </a>
          </nav>
          {/* Mobile Hamburger */}
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle navigation menu"
            className="md:hidden flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg border border-white/10 text-[#C8A24A] hover:bg-white/5 transition-colors duration-200"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {/* Mobile Menu Dropdown */}
        {menuOpen && (
          <div className="md:hidden border-t border-white/5 bg-[#0B0B0F]/95 backdrop-blur-xl" data-testid="mobile-menu">
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col">
              <a
                href="#how-it-works"
                onClick={() => setMenuOpen(false)}
                data-testid="mobile-nav-how-it-works"
                className="py-3 text-sm text-[#B0B0B0] hover:text-[#C8A24A] transition-colors duration-200 border-b border-white/5"
              >
                How It Works
              </a>
              <a
                href="#join-the-waitlist"
                onClick={() => setMenuOpen(false)}
                data-testid="mobile-nav-waitlist"
                className="py-3 text-sm text-[#B0B0B0] hover:text-[#C8A24A] transition-colors duration-200"
              >
                Waitlist
              </a>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative max-w-7xl mx-auto px-6 py-12 md:py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        {/* Hero Copy */}
        <div className="lg:col-span-7 flex flex-col space-y-8 text-left z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 w-fit">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C8A24A] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#C8A24A]" />
            </span>
            <span className="text-xs uppercase tracking-[0.15em] font-medium gold-text-metallic">
              Launching Soon
            </span>
          </div>

          <p className="text-xs tracking-[0.1em] text-[#8A8A8A] font-light -mt-4">
            Private Beta <span className="text-[#C8A24A]">•</span> Join the Global Waitlist
          </p>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-semibold font-clash text-white leading-tight">
            Meet the right <br />
            <span className="gold-text-metallic">
              people
            </span> <br />
            before anyone else.
          </h1>

          <div className="space-y-2 max-w-xl">
            <p className="text-lg text-[#B0B0B0] font-light leading-relaxed">
              Discover investors, business owners and professionals through meaningful business matching.
            </p>
            <p className="text-lg text-white/90 font-light leading-relaxed">
              Connecting ambitious people intentionally — anywhere in the world.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-4">
            <a 
              href="#join-the-waitlist" 
              data-testid="hero-join-waitlist-button"
              className="gold-metallic-surface text-[#0B0B0F] hover:scale-[1.02] font-bold rounded-full px-10 py-5 transition-transform duration-300 text-center flex items-center justify-center gap-2.5 text-base shadow-[0_10px_30px_-8px_rgba(200,162,74,0.4)] group"
            >
              <span className="relative z-10">Join the Waitlist</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform relative z-10" />
            </a>
            <a 
              href="#how-it-works" 
              className="text-[#B0B0B0] hover:text-[#C8A24A] font-medium px-4 py-5 transition-colors duration-300 text-center flex items-center justify-center gap-2 text-sm"
            >
              See How It Works
              <ArrowRight className="w-4 h-4 opacity-60" />
            </a>
          </div>
        </div>

        {/* Visual Mockup inside Smartphone Frame */}
        <div className="lg:col-span-5 flex justify-center items-start z-10 lg:-mt-28">
          <div className="relative w-[340px] h-[670px] bg-[#0F0F14] rounded-[50px] p-3 border-4 border-white/10 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.8)] flex flex-col overflow-hidden">
            {/* Top speaker & dynamic island detail */}
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-[#0B0B0F] rounded-2xl flex items-center justify-center z-40 border border-white/5">
              <div className="w-12 h-1 bg-white/10 rounded-full" />
            </div>

            {/* Inner Phone Screen Content */}
            <div className="flex-1 rounded-[40px] bg-[#0B0B0F] border border-white/5 overflow-hidden flex flex-col relative p-4">
              
              {/* Smartphone In-App Header */}
              <div className="flex items-center justify-between pt-6 pb-4 border-b border-white/5 px-2">
                <div className="flex flex-col">
                  <span className="text-xs font-clash tracking-[0.2em] text-white">SOMEWHERE</span>
                  <span className="text-[6px] tracking-[0.1em] text-[#C8A24A] uppercase -mt-0.5">by ANG</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#C8A24A] animate-pulse" />
                  <span className="text-[8px] text-[#B0B0B0] uppercase tracking-widest font-mono">LIVE DISCOVERY</span>
                </div>
              </div>

              {/* Card Container Stack */}
              <div className="flex-1 flex items-center justify-center relative mt-4">
                
                {/* Simulated Stack Background Cards */}
                <div className="absolute w-[90%] h-[92%] bg-[#121217] rounded-2xl border border-white/5 opacity-40 translate-y-6 scale-90 blur-[1px] pointer-events-none" />
                <div className="absolute w-[95%] h-[95%] bg-[#14141A] rounded-2xl border border-white/5 opacity-70 translate-y-3 scale-95 pointer-events-none" />

                {/* Main Interactive Card */}
                <AnimatePresence mode="popLayout">
                  {!isMatch && (
                    <motion.div
                      key={currentProfile.name}
                      initial={{ scale: 0.95, y: 0, opacity: 1 }}
                      animate={
                        swipeDirection === "right" 
                          ? { x: 400, rotate: 15, opacity: 0, scale: 0.95 }
                          : swipeDirection === "left"
                          ? { x: -400, rotate: -15, opacity: 0, scale: 0.95 }
                          : { x: 0, rotate: 0, opacity: 1, scale: 1 }
                      }
                      transition={{ duration: 0.55, ease: [0.23, 1, 0.32, 1] }}
                      className="absolute w-[100%] h-[100%] rounded-2xl bg-[#121217] border border-white/10 shadow-[0_15px_35px_rgba(0,0,0,0.6)] overflow-hidden flex flex-col"
                    >
                      {/* Avatar with luxury vignette */}
                      <div className="h-[200px] relative w-full bg-[#1A1A22] overflow-hidden">
                        <img 
                          src={currentProfile.image} 
                          alt={currentProfile.name} 
                          className="w-full h-full object-cover grayscale-[30%] brightness-[90%]"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#121217] via-transparent to-black/40" />
                        
                        {/* Category Badge */}
                        <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-[#0B0B0F]/80 backdrop-blur-md border border-[#C8A24A]/40">
                          <span className="text-[10px] text-[#C8A24A] tracking-wider uppercase font-semibold">
                            {currentProfile.category}
                          </span>
                        </div>

                        {/* Profile Summary info overlay */}
                        <div className="absolute bottom-3 left-3 right-3 text-left">
                          <h3 className="text-base font-semibold text-white tracking-tight leading-tight">
                            {currentProfile.name}
                          </h3>
                          <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-[#B0B0B0]">
                            <Briefcase className="w-3 h-3 text-[#C8A24A]" />
                            <span className="truncate">{currentProfile.designation} at {currentProfile.company}</span>
                          </div>
                        </div>
                      </div>

                      {/* Profile details */}
                      <div className="flex-1 p-3 text-left overflow-y-auto space-y-3.5 text-[11px]">
                        <div className="flex justify-between items-center text-[10px] text-[#8A8A8A] pb-1.5 border-b border-white/5">
                          <span className="flex items-center gap-1">
                            <Compass className="w-3 h-3 text-[#C8A24A]" /> {currentProfile.industry}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-[#C8A24A]" /> {currentProfile.location}
                          </span>
                        </div>

                        <div className="space-y-1">
                          <span className="text-[9px] uppercase tracking-widest text-[#C8A24A] font-semibold">ABOUT</span>
                          <p className="text-[#B0B0B0] leading-relaxed text-xs">{currentProfile.bio}</p>
                        </div>

                        <div className="p-2 rounded-lg bg-white/3 border border-white/5 space-y-0.5">
                          <span className="text-[8px] uppercase tracking-widest text-white/50 font-medium">{"WHAT I'M BUILDING"}</span>
                          <p className="text-[#DCC278] leading-snug">{currentProfile.building}</p>
                        </div>

                        <div className="p-2 rounded-lg bg-[#C8A24A]/5 border border-[#C8A24A]/10 space-y-0.5">
                          <span className="text-[8px] uppercase tracking-widest text-[#C8A24A] font-medium">{"LOOKING FOR"}</span>
                          <p className="text-[#B0B0B0] leading-snug">{currentProfile.looking}</p>
                        </div>
                      </div>

                      {/* Simulated In-App Actions */}
                      <div className="p-3 border-t border-white/5 flex items-center justify-around bg-[#0F0F14]">
                        <button className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-[#B0B0B0] transition-colors duration-200">
                          <span className="text-xs font-semibold">Pass</span>
                        </button>
                        <button className="w-11 h-11 rounded-full bg-[#C8A24A]/10 hover:bg-[#C8A24A]/20 border border-[#C8A24A]/40 flex items-center justify-center text-[#C8A24A] transition-colors duration-200 shadow-[0_0_15px_rgba(200,162,74,0.1)]">
                          <Heart className="w-5 h-5 fill-current" />
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* "It's a Match!" Overlay Animation */}
                <AnimatePresence>
                  {isMatch && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      className="absolute inset-0 rounded-2xl glass-panel-gold z-30 flex flex-col items-center justify-center p-6 text-center"
                    >
                      <motion.div
                        initial={{ y: -15 }}
                        animate={{ y: 0 }}
                        className="space-y-1 mb-6"
                      >
                        <h2 className="text-2xl font-semibold font-clash text-white tracking-wider">Connection Unlocked</h2>
                      </motion.div>

                      {/* Matching Avatars */}
                      <div className="flex items-center justify-center space-x-[-16px] mb-6">
                        <div className="w-16 h-16 rounded-full bg-[#121217] border-2 border-[#C8A24A] flex items-center justify-center shadow-lg">
                          <span className="text-xs font-clash font-semibold text-[#C8A24A] tracking-widest">YOU</span>
                        </div>
                        <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#C8A24A] shadow-lg">
                          <img 
                            src={PROFILES[0].image} 
                            alt="Matched Profile" 
                            className="w-full h-full object-cover grayscale-[20%]"
                          />
                        </div>
                      </div>

                      <div className="space-y-4 w-full">
                        <div className="space-y-1">
                          <p className="text-[11px] text-white leading-relaxed max-w-[200px] mx-auto">
                            You and Sehaj Singh are now connected.
                          </p>
                          <p className="text-[10px] text-[#B0B0B0] leading-relaxed max-w-[200px] mx-auto">
                            Choose how you'd like to start the conversation.
                          </p>
                        </div>

                        <div className="flex flex-col gap-2 w-full max-w-[210px] mx-auto pt-1">
                          <button className="gold-metallic-surface text-[#0B0B0F] font-semibold text-[10px] tracking-wider uppercase rounded-full px-4 py-2.5 flex items-center justify-center gap-1.5">
                            <MessageCircle className="w-3.5 h-3.5 relative z-10" />
                            <span className="relative z-10">Connect on WhatsApp</span>
                          </button>
                          <button className="border border-[#C8A24A]/50 text-[#C8A24A] hover:bg-[#C8A24A]/10 font-semibold text-[10px] tracking-wider uppercase rounded-full px-4 py-2.5 flex items-center justify-center gap-1.5 transition-colors duration-200">
                            <Mail className="w-3.5 h-3.5" />
                            Connect via Email
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Strip */}
      <section className="border-t border-white/5 bg-black/30 py-12 relative" data-testid="social-proof-section">
        <div className="max-w-5xl mx-auto px-6 flex flex-col items-center gap-8">
          <span className="text-xs uppercase tracking-[0.25em] gold-text-metallic font-semibold">Most Waitlist Sign-ups From</span>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            {[
              { flag: "🇦🇪", name: "UAE" },
              { flag: "🇸🇦", name: "Saudi Arabia" },
              { flag: "🇮🇳", name: "India" },
              { flag: "🇬🇧", name: "United Kingdom" },
              { flag: "🇺🇸", name: "United States" }
            ].map((country) => (
              <div 
                key={country.name}
                className="flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-white/[0.03] border border-white/10 hover:border-[#C8A24A]/40 hover:bg-white/[0.05] transition-all duration-300"
              >
                <span className="text-lg leading-none">{country.flag}</span>
                <span className="text-sm text-[#B0B0B0] font-medium whitespace-nowrap">{country.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credibility Statement */}
      <div className="border-t border-white/5 py-10 relative">
        <p className="max-w-2xl mx-auto px-6 text-center text-sm text-[#8A8A8A] font-light leading-relaxed">
          Built by <span className="text-[#C8A24A]/90">Arabian Networking Group</span> — creators of multi-category business networking events across the GCC.
        </p>
      </div>

      {/* Why SOMEWHERE Section */}
      <section className="border-t border-white/5 bg-black/40 py-20 relative">
        <div className="max-w-4xl mx-auto px-6 text-center space-y-6">
          <span className="text-xs uppercase tracking-[0.2em] text-[#C8A24A] font-semibold">Our Mission</span>
          <h2 className="text-3xl sm:text-4xl font-semibold font-clash text-white">Why SOMEWHERE?</h2>
          <p className="text-base text-[#B0B0B0] font-light leading-relaxed max-w-2xl mx-auto">
            Built by the team behind Arabian Networking Group's premium business networking events across the GCC, SOMEWHERE brings intentional business networking into one intelligent platform—helping investors, business owners and professionals build meaningful relationships from anywhere in the world.
          </p>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="bg-[#121217]/60 border-y border-white/5 py-24 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-xl mx-auto mb-16 space-y-4">
            <span className="text-xs uppercase tracking-[0.2em] text-[#C8A24A] font-semibold">How It Works</span>
            <h2 className="text-3xl sm:text-4xl font-semibold font-clash text-white">How SOMEWHERE Works</h2>
            <p className="text-sm text-[#B0B0B0] leading-relaxed">
              A focused, efficient way to discover relevant business people and build meaningful professional relationships, designed entirely around real business outcomes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="bg-[#121217] border border-white/5 rounded-2xl p-8 hover:border-[#C8A24A]/30 transition-all duration-300 group text-left">
              <div className="w-12 h-12 rounded-full bg-[#C8A24A]/5 border border-[#C8A24A]/20 flex items-center justify-center text-[#C8A24A] mb-6 group-hover:bg-[#C8A24A]/10 transition-colors">
                <Compass className="w-5 h-5" />
              </div>
              <h3 className="text-xl font-medium text-white mb-3">1. Discover</h3>
              <p className="text-sm text-[#B0B0B0] leading-relaxed">
                Browse curated profiles of investors, business owners and professionals matched to your goals.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-[#121217] border border-white/5 rounded-2xl p-8 hover:border-[#C8A24A]/30 transition-all duration-300 group text-left">
              <div className="w-12 h-12 rounded-full bg-[#C8A24A]/5 border border-[#C8A24A]/20 flex items-center justify-center text-[#C8A24A] mb-6 group-hover:bg-[#C8A24A]/10 transition-colors">
                <Heart className="w-5 h-5" />
              </div>
              <h3 className="text-xl font-medium text-white mb-3">2. Express Interest</h3>
              <p className="text-sm text-[#B0B0B0] leading-relaxed">
                Swipe right to express genuine interest or swipe left to skip. Every interaction is intentional.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-[#121217] border border-white/5 rounded-2xl p-8 hover:border-[#C8A24A]/30 transition-all duration-300 group text-left">
              <div className="w-12 h-12 rounded-full bg-[#C8A24A]/5 border border-[#C8A24A]/20 flex items-center justify-center text-[#C8A24A] mb-6 group-hover:bg-[#C8A24A]/10 transition-colors">
                <Sparkles className="w-5 h-5" />
              </div>
              <h3 className="text-xl font-medium text-white mb-3">3. Connect Instantly</h3>
              <p className="text-sm text-[#B0B0B0] leading-relaxed">
                When interest is mutual, connect immediately via WhatsApp or Email and start meaningful conversations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Waitlist Signup Section */}
      <section id="join-the-waitlist" className="max-w-4xl mx-auto px-6 py-24 md:py-32">
        <div className="glass-panel-gold rounded-3xl p-8 md:p-12 text-center space-y-10 relative">
          
          <div className="max-w-xl mx-auto space-y-4">
            <h2 className="text-3xl sm:text-4xl font-semibold font-clash text-white">Join the Waitlist</h2>
            <p className="text-sm text-[#B0B0B0] leading-relaxed">
              Join the global waitlist for early access to SOMEWHERE.
            </p>
            <p className="text-sm text-[#8A8A8A] leading-relaxed">
              You'll receive an email confirming your waitlist sign-up. If you're among the first 500 waitlist sign-ups worldwide, your confirmation email will also unlock complimentary unlimited swipes for your first 3 months after launch.
            </p>
          </div>

          {/* Launch Benefit */}
          <div className="max-w-2xl mx-auto w-full" data-testid="launch-benefit-card">
            <div className="rounded-2xl border border-[#C8A24A]/25 bg-[#C8A24A]/[0.04] px-6 py-5 text-left flex items-start gap-4">
              <div className="w-9 h-9 rounded-full bg-[#C8A24A]/10 border border-[#C8A24A]/30 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 text-[#C8A24A]" />
              </div>
              <div className="space-y-1">
                <span className="text-xs uppercase tracking-[0.18em] gold-text-metallic font-semibold">Launch Benefit</span>
                <p className="text-sm text-[#B0B0B0] leading-relaxed">
                  <span className="font-semibold text-white">First 500 waitlist sign-ups worldwide</span> will receive complimentary unlimited swipes for their first 3 months after launch.
                </p>
              </div>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {!submitted ? (
              <motion.form 
                onSubmit={handleSubmit}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-6 text-left max-w-2xl mx-auto"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Full Name */}
                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-widest text-[#B0B0B0] font-semibold flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-[#C8A24A]" /> Full Name
                    </label>
                    <input 
                      type="text" 
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="e.g. Sara Al-Mansoori"
                      data-testid="waitlist-name-input"
                      className="w-full bg-black/40 border border-white/10 focus:border-[#C8A24A] hover:border-white/20 transition-all rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#C8A24A]"
                    />
                  </div>

                  {/* Company */}
                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-widest text-[#B0B0B0] font-semibold flex items-center gap-1.5">
                      <Building className="w-3.5 h-3.5 text-[#C8A24A]" /> Company
                    </label>
                    <input 
                      type="text" 
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="e.g. Aura Capital"
                      data-testid="waitlist-company-input"
                      className="w-full bg-black/40 border border-white/10 focus:border-[#C8A24A] hover:border-white/20 transition-all rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#C8A24A]"
                    />
                  </div>

                  {/* Designation */}
                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-widest text-[#B0B0B0] font-semibold flex items-center gap-1.5">
                      <Briefcase className="w-3.5 h-3.5 text-[#C8A24A]" /> Designation
                    </label>
                    <input 
                      type="text" 
                      name="designation"
                      value={formData.designation}
                      onChange={handleChange}
                      placeholder="e.g. Managing Partner"
                      data-testid="waitlist-designation-input"
                      className="w-full bg-black/40 border border-white/10 focus:border-[#C8A24A] hover:border-white/20 transition-all rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#C8A24A]"
                    />
                  </div>

                  {/* Email Address */}
                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-widest text-[#B0B0B0] font-semibold flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5 text-[#C8A24A]" /> Email Address
                    </label>
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="sara@auracapital.com"
                      data-testid="waitlist-email-input"
                      className="w-full bg-black/40 border border-white/10 focus:border-[#C8A24A] hover:border-white/20 transition-all rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#C8A24A]"
                    />
                  </div>
                </div>

                {/* Phone Number */}
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-widest text-[#B0B0B0] font-semibold flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5 text-[#C8A24A]" /> Phone Number
                  </label>
                  <input 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+971 50 123 4567"
                    data-testid="waitlist-phone-input"
                    className="w-full bg-black/40 border border-white/10 focus:border-[#C8A24A] hover:border-white/20 transition-all rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#C8A24A]"
                  />
                </div>

                <div className="pt-4">
                  <button 
                    type="submit" 
                    disabled={loading}
                    data-testid="waitlist-submit-button"
                    className="w-full bg-[#C8A24A] text-[#0B0B0F] hover:bg-[#DCC278] disabled:opacity-50 font-bold rounded-lg px-6 py-4 transition-all duration-300 flex items-center justify-center gap-2 tracking-wider uppercase text-xs"
                  >
                    {loading ? "Verifying Credentials..." : "Join the Waitlist"}
                  </button>
                </div>

                {/* Complimentary message */}
                <p className="text-xs text-[#8A8A8A] text-center leading-relaxed max-w-lg mx-auto pt-2">
                  You'll receive an email confirming your waitlist sign-up. If you're among the first 500 waitlist sign-ups worldwide, your confirmation email will also unlock complimentary unlimited swipes for your first 3 months after launch.
                </p>
              </motion.form>
            ) : (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                data-testid="waitlist-success-message"
                className="p-8 bg-[#C8A24A]/10 border border-[#C8A24A]/30 rounded-2xl max-w-lg mx-auto space-y-4"
              >
                <div className="w-12 h-12 rounded-full bg-[#C8A24A] text-[#0B0B0F] flex items-center justify-center mx-auto shadow-md">
                  <Check className="w-6 h-6 stroke-[3]" />
                </div>
                <h3 className="text-xl font-semibold font-clash text-white tracking-wide">You're officially on the Global Waitlist! 🌍</h3>
                <p className="text-sm text-[#B0B0B0] leading-relaxed">
                  Keep an eye on your inbox—we'll send you a confirmation email shortly.
                </p>
                <p className="text-sm text-[#B0B0B0] leading-relaxed">
                  If you're among the first 500 waitlist sign-ups worldwide, we'll let you know in that email that you've unlocked complimentary unlimited swipes for your first 3 months after launch.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Trust Statement */}
      <div className="border-t border-white/5 bg-black/40 py-8 relative">
        <p className="max-w-3xl mx-auto px-6 text-center text-xs sm:text-sm text-[#8A8A8A] font-light tracking-wide leading-relaxed">
          Built by the team behind Arabian Networking Group's multi-category business networking events across the GCC.
        </p>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 bg-black/60 relative">
        <p className="text-center text-xs uppercase tracking-[0.2em] gold-text-metallic font-medium mb-10" data-testid="launching-soon-line">
          Launching soon worldwide.
        </p>
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src="/ang-logo-gold.png" alt="Arabian Networking Group" className="h-10 w-auto object-contain" />
            <div className="flex flex-col items-start">
              <span className="text-sm font-semibold tracking-wider font-clash gold-text-metallic">SOMEWHERE</span>
              <span className="text-[9px] uppercase tracking-[0.15em] text-[#B0B0B0] font-medium font-inter">
                by Arabian Networking Group
              </span>
            </div>
          </div>
          <div className="flex flex-col items-center md:items-end gap-2">
            <div className="flex items-center gap-4">
              <Link to="/privacy" data-testid="footer-privacy-link" className="text-xs text-[#8A8A8A] hover:text-[#C8A24A] transition-colors duration-200">
                Privacy Policy
              </Link>
              <span className="text-[#8A8A8A]/40 text-xs">•</span>
              <Link to="/terms" data-testid="footer-terms-link" className="text-xs text-[#8A8A8A] hover:text-[#C8A24A] transition-colors duration-200">
                Terms of Use
              </Link>
            </div>
            <p className="text-xs text-[#8A8A8A] text-center md:text-right font-light">
              Powered by Arabian Networking Group.<br />
              © {new Date().getFullYear()} Arabian Networking Group. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
